import { useMemo, forwardRef } from 'react';

const UID = () => {
  return 'xxxx-xxxx-xxx-xxxx'.replace(/[x]/g, () => {
    const r = Math.floor(Math.random() * 16);
    return r.toString(16);
  });
};

const useUID = () => useMemo(() => UID(), []);

type RadioProps = {
  label?: React.ReactNode;
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>;
  wrapperClassName?: string;
  frame?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>;

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      label,
      wrapperClassName,
      frame,
      disabled,
      className,
      ...inputProps
    }: RadioProps,
    ref,
  ) => {
    const uid = useUID();

    const wrapperClasses = [
      'relative',
      frame &&
        'border border-structure-80 rounded-s overflow-hidden hover:bg-secondary-95 has-[:checked]:border-primary-40',
      frame &&
        disabled &&
        'bg-structure-95 border-structure-80 cursor-not-allowed hover:bg-structure-95',
      wrapperClassName,
    ]
      .filter(Boolean)
      .join(' ');

    const radioClasses = [
      'appearance-none inline-block m-0 align-middle w-6 h-6 shrink-0 p-0 rounded-full',
      'border border-solid border-structure-60 bg-structure-100 cursor-pointer',
      'hover:shadow-[inset_0_0_0_3px_rgba(255,255,255,1),inset_0_0_0_24px_rgba(238,244,247,1)]',
      'checked:shadow-[inset_0_0_0_3px_rgba(255,255,255,1),inset_0_0_0_24px_rgba(32,94,172,1)]',
      'focus-visible:outline-none',
      'disabled:bg-structure-95 disabled:border-structure-60 disabled:cursor-not-allowed disabled:shadow-none',
      'checked:disabled:shadow-[inset_0_0_0_3px_rgba(255,255,255,1),inset_0_0_0_24px_rgba(153,153,153,1)]',
      frame && 'absolute left-m top-1/2 -translate-y-1/2',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClasses}>
        <input
          type="radio"
          className={radioClasses}
          disabled={disabled}
          ref={ref}
          id={uid}
          {...inputProps}
        />
        {label}
      </div>
    );
  },
);

Radio.displayName = 'Radio';
